import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef
} from '@angular/core';
import { HistoricalChartComponent } from 'app/charts-dashboard/components/charts/historical-chart/historical-chart.component';
import { CommonService } from 'app/core/services/common.service';
import { ChartsCommonService } from 'app/charts-dashboard/services/charts-common.service';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries
} from 'app/charts-dashboard/state/series';
import { ChartsDashboardCardQuery } from 'app/charts-dashboard/state/card';
import { ChartsHttpService } from 'app/charts-dashboard/services/charts-http.service';
import {
  AxisXHistorical,
  Datetime,
  IChartFilter,
  IChartPointsResponse,
  IIvDifferenceChartParameters,
  ILCPoint,
  IMappedSeriesPointsResponse,
} from 'app/charts-dashboard/interfaces';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  LineSeries,
  SeriesXY
} from '@arction/lcjs';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-iv-difference',
  templateUrl: '../common-chart/common-chart.component.html',
  styleUrls: ['../common-chart/common-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IvDifferenceComponent extends HistoricalChartComponent {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly httpService: ChartsHttpService,
    protected readonly chartDashboardState: ChartsDashboardStateService
  ) {
    super(commonService, chartsCommonService, elementRef, changeDetectorRef, dashboardQuery, seriesQuery, cardsQuery, httpService, chartDashboardState);
  }

  protected createSeriesObservables(seriesArr: IChartSeries[], isFirstLoad?: boolean): Observable<IMappedSeriesPointsResponse<AxisXHistorical>>[] {
    return seriesArr.map((series: IChartSeries) => {
      return this.httpService.getIvDifference(this.getChartsParams(series, isFirstLoad) as IIvDifferenceChartParameters)
        .pipe(map((data: IChartPointsResponse<AxisXHistorical>) => {
          return {
            ...data,
            seriesId: series.id
          };
        }));
    });
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[]): void {
    if (!lineSeries) return;

    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        const dateTimeRow = this.getDateTimeRow(_series,x)
        this.builderCursorResult
          .addRow(`${this.cursorResult.x === 'Date' ? '' : this.cursorResult.x} ${dateTimeRow}`.replace(',', ''))
          .addRow('--- Point 1 ---')
          .addRow(`Symbol: ${series.selectedFilters['stockId-first'].symbol}`)
          .addRow(`Period: ${series.selectedFilters['ivxTerm-first'].description}`)
          .addRow(`Moneyness: ${series.selectedFilters['moneyness-first'].description}`)
          .addRow('--- Point 2 ---')
          .addRow(`Symbol: ${series.selectedFilters['stockId-second'].symbol}`)
          .addRow(`Period: ${series.selectedFilters['ivxTerm-second'].description}`)
          .addRow(`Moneyness: ${series.selectedFilters['moneyness-second'].description}`)
          .addRow(`${this.cursorResult.y}: ${Math.round((y + Number.EPSILON) * 100) / 100}`);

        return this.builderCursorResult;
      });
  }

  protected getChartsParams(series: IChartSeries, isFirstLoad?: boolean): IIvDifferenceChartParameters {
    if (!series) return;

    let from: Datetime;

    if (!isFirstLoad) {
      from = this.getFrom(series);
    }

    return {
      timeRange: series.selectedFilters.timeRange.name,
      from,
      firstStockId: series.selectedFilters['stockId-first'].stockId,
      firstSymbol: series.selectedFilters['stockId-first'].symbol,
      firstIvxTerm: series.selectedFilters['ivxTerm-first'].name,
      firstMoneyness: series.selectedFilters['moneyness-first'].name,
      secondStockId: series.selectedFilters['stockId-second'].stockId,
      secondSymbol: series.selectedFilters['stockId-second'].symbol,
      secondIvxTerm: series.selectedFilters['ivxTerm-second'].name,
      secondMoneyness: series.selectedFilters['moneyness-second'].name,
      sendUserMetrics: !this.isSentUserMetrics && this.mainSeries.id === series.id
    };
  }
}
